import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { ColorScheme, Map, useMap } from '@vis.gl/react-google-maps'
import { GoogleMapsOverlay } from '@deck.gl/google-maps'
import { DeckProps } from '@deck.gl/core'
import { HeatmapLayer } from '@deck.gl/aggregation-layers'
import {
  MilesByZipcodeData,
  MilesByZipcodeSummary,
} from 'interfaces/ISummaryReport'
import { getHeatmapColorRange, DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM } from 'tools/mapHelper'

interface IMilesByZipcodeMapProps {
  summary: MilesByZipcodeSummary | null
  data: MilesByZipcodeData[]
}

const DeckGLOverlay = (props: DeckProps) => {
  const map = useMap()
  const overlay = useMemo(() => new GoogleMapsOverlay(props), [])

  useEffect(() => {
    if (map) {
      overlay.setMap(map)
      return () => overlay.setMap(null)
    }
  }, [map, overlay])

  return null
}

const MilesByZipcodeMap: React.FC<IMilesByZipcodeMapProps> = ({
  summary,
  data,
}) => {
  const map = useMap()
  const [defaultZoom] = useState(DEFAULT_MAP_ZOOM)
  const [defaultCenter] = useState(DEFAULT_MAP_CENTER)
  const [currentZoom, setCurrentZoom] = useState(DEFAULT_MAP_ZOOM)

  const milesByZipcodeData = useMemo(() => {
    if (!data || data.length === 0) return []
    return data.map((item) => ({
      position: [item.centralPoint.longitude, item.centralPoint.latitude],
      id: item.rowId,
      miles: item.metrics.distanceMiles,
    }))
  }, [data])

  const geoJsonPoints = useMemo(() => {
    if (!milesByZipcodeData || milesByZipcodeData.length === 0) return []
    return milesByZipcodeData.map((item) => ({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: item.position },
      properties: { miles: item.miles },
    }))
  }, [milesByZipcodeData])

  const calculateHeatmapWeight = (d: any) => {
    if (!d.properties?.miles || !summary?.globalTotalMiles) return 0

    const miles = d.properties.miles
    const totalMiles = summary.globalTotalMiles
    if (totalMiles === 0) return 0

    const percentage = miles / totalMiles

    if (percentage >= 0.1) {
      return Math.max(0.02, Math.min(1.0, percentage * 10))
    } else if (percentage >= 0.01) {
      const scaledWeight = percentage * 500
      return Math.max(0.02, Math.log10(scaledWeight + 1) / Math.log10(501))
    } else if (percentage >= 0.001) {
      const scaledWeight = percentage * 2000
      return Math.max(0.02, Math.log10(scaledWeight + 1) / Math.log10(2001))
    } else if (percentage >= 0.0001) {
      const scaledWeight = percentage * 8000
      return Math.max(0.02, Math.log10(scaledWeight + 1) / Math.log10(8001))
    } else {
      const scaledWeight = percentage * 20000
      return Math.max(
        0.01,
        Math.min(0.15, Math.log10(scaledWeight + 1) / Math.log10(20001))
      )
    }
  }

  const layers = useMemo(() => {
    if (!geoJsonPoints || geoJsonPoints.length === 0) return []
    return [
      new HeatmapLayer({
        data: geoJsonPoints,
        getPosition: (d) => d.geometry.coordinates,
        getWeight: calculateHeatmapWeight,
        radiusPixels: 25,
        intensity: 2,
        threshold: 0.01,
        weightsTextureSize: 512,
        debounceTimeout: 300,
        colorRange: getHeatmapColorRange(),
      }),
    ]
  }, [geoJsonPoints, summary])

  return (
    <div style={{ height: '350px', width: '100%' }}>
      <Map
        defaultZoom={defaultZoom}
        defaultCenter={defaultCenter}
        gestureHandling={'greedy'}
        disableDefaultUI={true}
        clickableIcons={false}
        mapId={'MILES_BY_ZIPCODE_MAP'}
        colorScheme={ColorScheme.DARK}
      >
        <DeckGLOverlay layers={layers} />
      </Map>
    </div>
  )
}

export default MilesByZipcodeMap
